import BackendService from 'BackendService';
import Contact from '../../../models/Contact';
import { DRAFT_KEY, InvestorDraft, contactLabel } from './veraGlobalChatPromptOverrideUtils';

export function syncPreviewPrompt(chatId: string, markdown: string): Promise<void> {
  if (!markdown.trim()) return Promise.resolve();
  return new BackendService().saveVeraPreviewPrompt(chatId, markdown);
}

export function syncIntentPrompt(text: string): Promise<void> {
  if (!text.trim()) return Promise.resolve();
  return new BackendService().saveVeraIntentPrompt(text).then(() => undefined);
}

export function syncGroundingPrompt(chatId: string, text: string): Promise<void> {
  if (!text.trim()) return Promise.resolve();
  return new BackendService().saveVeraPreviewGroundingPrompt(chatId, text);
}

export function syncGoogleSearch(chatId: string, enabled: boolean): Promise<void> {
  return new BackendService().saveVeraPreviewGoogleSearch(chatId, enabled);
}

export async function ensureInvestorPreviewSynced(
  chatId: string,
  markdown: string,
  intentText: string,
  groundingText: string,
  sandboxSearchEnabled: boolean,
  sandboxSearchLocked: boolean,
): Promise<void> {
  const tasks: Promise<unknown>[] = [];
  if (markdown.trim()) tasks.push(syncPreviewPrompt(chatId, markdown));
  if (intentText.trim()) tasks.push(syncIntentPrompt(intentText));
  if (groundingText.trim()) tasks.push(syncGroundingPrompt(chatId, groundingText));
  if (sandboxSearchEnabled && !sandboxSearchLocked) {
    tasks.push(syncGoogleSearch(chatId, true));
  }
  if (tasks.length === 0) return;
  await Promise.all(tasks);
}

export function scheduleInvestorDraftSave(
  userHasEdited: boolean,
  draft: Omit<InvestorDraft, 'savedAt'>,
  debounceRef: { current: ReturnType<typeof setTimeout> | null },
): void {
  if (!userHasEdited) return;
  if (debounceRef.current) clearTimeout(debounceRef.current);
  debounceRef.current = setTimeout(() => {
    localStorage.setItem(
      DRAFT_KEY,
      JSON.stringify({ ...draft, savedAt: new Date().toISOString() } satisfies InvestorDraft),
    );
  }, 1500);
}

export function clearInvestorDraftStorage(): void {
  localStorage.removeItem(DRAFT_KEY);
}

export function refreshPreviewGoogleSearch(chatId: string): Promise<{
  sandboxEnabled: boolean;
  sandboxToggleLocked: boolean;
}> {
  return new BackendService().getVeraPreviewGoogleSearch(chatId);
}

export function searchContacts(query: string): Promise<Contact[]> {
  return new BackendService().searchContact(query, true);
}
